import React, { useState, ReactNode, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export interface TabItem {
  id: string | number;
  header: ReactNode;
  content: ReactNode;
}

interface ETabsProps {
  tabs: TabItem[];
  initialActiveIndex?: number;
  onTabChange?: (index: number) => void;
}

export const ETabs: React.FC<ETabsProps> = ({
  tabs,
  initialActiveIndex = 0,
  onTabChange,
}) => {
  const [activeIndex, setActiveIndex] = useState(initialActiveIndex);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const handleTabClick = (index: number) => {
    if (index === activeIndex) return; // Gereksiz re-render önle
    setActiveIndex(index);
    if (onTabChange) {
      onTabChange(index);
    }
  };

  const checkForScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(Math.ceil(scrollLeft + clientWidth) < scrollWidth);
    }
  };

  useEffect(() => {
    checkForScroll();
    window.addEventListener('resize', checkForScroll);
    return () => window.removeEventListener('resize', checkForScroll);
  }, [tabs]);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const { clientWidth } = scrollContainerRef.current;
      const scrollAmount = clientWidth * 0.6; // Ekranın %60'ı kadar kaydır
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="w-full flex flex-col card p-0 bg-card border-borderColor relative">
      {/* Tabs Header Container */}
      <div className="relative w-full border-b border-borderColor bg-bgAlt rounded-t-lg group">
        
        {/* Left Scroll Button */}
        {canScrollLeft && (
          <div className="absolute left-0 top-0 bottom-0 z-30 flex items-center bg-gradient-to-r from-bgAlt via-bgAlt to-transparent w-16 pl-2">
            <button onClick={() => scroll('left')} className="p-1.5 rounded-full bg-card shadow-md border border-borderColor text-text opacity-80 hover:opacity-100 hover:text-primary hover:bg-bgAlt transition-all">
              <ChevronLeft size={16} />
            </button>
          </div>
        )}

        {/* Scrollable Tabs */}
        <div 
          ref={scrollContainerRef}
          onScroll={checkForScroll}
          className="flex overflow-x-auto hide-scrollbar gap-2 px-3 pt-2 relative z-10 scroll-smooth"
        >
          {tabs.map((tab, index) => {
            const isActive = index === activeIndex;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabClick(index)}
                className={`
                  relative flex items-center justify-center gap-2 px-5 py-3.5 text-[14px] font-bold transition-colors duration-200 whitespace-nowrap outline-none
                  ${isActive
                    ? 'text-primary'
                    : 'text-text opacity-70 hover:opacity-100 hover:text-primary hover:bg-bgAlt rounded-t-xl'
                  }
                `}
                role="tab"
                aria-selected={isActive}
              >
                {/* Tab Label */}
                <span className="relative z-10 flex items-center gap-2">
                  {tab.header}
                </span>

                {/* Sliding Bottom Line */}
                {isActive && (
                  <motion.div
                    layoutId="activeTabIndicator"
                    className="absolute bottom-[-1px] left-0 right-0 h-[3px] bg-primary rounded-t-full z-20 shadow-sm"
                    initial={false}
                    transition={{ type: "spring", stiffness: 500, damping: 35 }}
                  />
                )}
                
                {/* Optional subtle active background glow */}
                {isActive && (
                  <motion.div
                    layoutId="activeTabBg"
                    className="absolute inset-0 bg-bgAlt rounded-t-xl z-0"
                    initial={false}
                    transition={{ type: "spring", stiffness: 500, damping: 35 }}
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* Right Scroll Button */}
        {canScrollRight && (
          <div className="absolute right-0 top-0 bottom-0 z-30 flex items-center justify-end bg-gradient-to-l from-bgAlt via-bgAlt to-transparent w-16 pr-2">
            <button onClick={() => scroll('right')} className="p-1.5 rounded-full bg-card shadow-md border border-borderColor text-text opacity-80 hover:opacity-100 hover:text-primary hover:bg-bgAlt transition-all">
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Tabs Content - Smooth Crossfade and Slide */}
      <div className="w-full overflow-hidden p-2">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeIndex}
            initial={{ opacity: 0, y: 15, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -15, scale: 0.98 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="w-full"
          >
            {tabs[activeIndex] && tabs[activeIndex].content}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};
